import type { RuleSet } from '../../../rules';
import {
  type Building,
  buildingLevel,
  canAfford,
  capCount,
  capType,
  milliCost,
  type PlayerId,
  type SimState,
  type UnitType,
  unitCap,
  unitCost,
  unitUnlocked,
} from '../../../sim';
import { cityName, ownCities } from '../../cities';
import type { Names } from '../../names';
import { overCapText } from '../../reasons';
import type { GameEvent } from '../types';

/**
 * The "unit cap reached" condition: one alert per capped unit type the
 * viewer is blocked from training by the cap (see `capAlerts`). Like food, it is a
 * condition: the feed keeps one entry per key while it holds and drops it
 * once the count falls below the cap (feed.ts, `addEvents`).
 */

/** Condition keys of the cap alerts: this prefix and the cap type. */
export const CAP_KEY_PREFIX = 'cap:';

/**
 * The cap alerts for this frame, one per cap type whose count (queued units
 * included, as `capCount`) has reached a finite `unitCap` and where that cap
 * is what stops training: some completed city of the viewer's trains the type
 * (or one sharing the cap, `capAs`), has it unlocked and the viewer can pay
 * for it now. Sitting at a cap with nothing to spend, or on a type out of
 * reach, is routine and raises nothing. Named for the type the cap is shared
 * under. Over the cap (a city was lost) it says so, from `overCapText`. Each
 * opens the city that trains it: the lowest-level one when the cap grows with
 * city level (raising it is the way out), else the lowest-numbered.
 */
export function capAlerts(
  state: SimState,
  rules: RuleSet,
  playerId: PlayerId,
  cityNumbers: ReadonlyMap<string, number>,
  names?: Names
): GameEvent[] {
  // The cap and count per cap type, so the cheap test runs first and once.
  const blocked = new Map<UnitType, { cap: number; count: number } | null>();
  let cities: Building[] | undefined;
  const events = new Map<UnitType, GameEvent>();
  for (const type of Object.keys(rules.units)) {
    const shared = capType(rules, type);
    if (events.has(shared)) continue;
    if (!blocked.has(shared)) {
      const cap = unitCap(state, rules, playerId, type);
      const count = Number.isFinite(cap) ? capCount(state, rules, playerId, type) : 0;
      blocked.set(shared, Number.isFinite(cap) && count >= cap ? { cap, count } : null);
    }
    const hit = blocked.get(shared);
    if (!hit) continue;
    cities ??= ownCities(state, rules, playerId);
    const cost = milliCost(unitCost(state, rules, playerId, type));
    if (!canAfford(state, playerId, cost)) continue;
    const def = rules.units[shared];
    const byLevel = (def?.perCityLevel ?? 0) > 0;
    const city = cities
      .filter(
        (c) =>
          rules.buildings[c.type]?.spawns?.includes(type) && unitUnlocked(state, rules, c.id, type)
      )
      .sort(
        (a, b) =>
          (byLevel ? buildingLevel(a) - buildingLevel(b) : 0) ||
          (cityNumbers.get(a.id) ?? Number.POSITIVE_INFINITY) -
            (cityNumbers.get(b.id) ?? Number.POSITIVE_INFINITY)
      )[0];
    if (!city) continue;
    const { cap, count } = hit;
    const over = overCapText(count, cap);
    const grows = byLevel ? ' or raise a city’s level' : '';
    events.set(shared, {
      kind: 'cap',
      key: `${CAP_KEY_PREFIX}${shared}`,
      text: `${def?.name ?? shared} ${over ? 'over cap' : 'cap reached'} (${count}/${cap})`,
      detail: over?.title ?? `Found another city${grows} to allow more.`,
      cityId: city.id,
      action: `Open ${cityName(city, playerId, cityNumbers, undefined, names)}`,
    });
  }
  return [...events.values()];
}
